import {
  memo,
  forwardRef,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { Box, Stack, Typography, Chip } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Virtuoso } from "react-virtuoso";
import { colors, shadows } from "./Pipeline.styles";
import SortablePipelineCard from "./SortablePipelineCard";
import PipelineColumnMenu from "./PipelineColumnMenu";
import PipelineCard from "./PipelineCard";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

// Define Scroller outside component to avoid recreation on every render
// Note: scrollbar colors are applied via CSS in the Virtuoso wrapper
const Scroller = forwardRef(({ children, style, ...props }, ref) => (
  <Box
    {...props}
    ref={ref}
    sx={{
      ...style,
      scrollbarWidth: "thin",
      "&::-webkit-scrollbar": {
        width: "8px",
      },
      "&::-webkit-scrollbar-track": {
        bgcolor: "#FFFFFF !important" ,
      },
      "&::-webkit-scrollbar-thumb": {
        bgcolor: colors.primary,
        borderRadius: "4px",
        "&:hover": {
          bgcolor: colors.primaryDark,
        },
      },
    }}
  >
    {children}
  </Box>
));

Scroller.displayName = "Scroller";

// Footer component to add bottom padding for last card visibility
const Footer = () => <div style={{ height: 80 }} />;

const ITEM_HEIGHT = 170; // Approximate card height for positioning

const PipelineColumn = memo(
  ({
    title,
    leads = [],
    columnId,
    sortOption = "date-newest",
    columnSettings = null,
    onViewDetails,
    onContactClick,
    onAddLead,
    onMenuAction,
    onColumnMenuAction,
    insertPosition = null,
    activeLead = null, // The card being dragged
    bulkSelectMode = false,
    selectedLeadIds = [],
    onSelectToggle,
    showDropZone = false, // Show drop zone for bulk drag
    dropZoneCount = 0, // Number of cards being dropped
    isFirstColumn = false,
    isLastColumn = false,
  }) => {
    const theme = useTheme();

    // Sort leads based on sortOption
    const sortedLeads = useMemo(() => {
      const leadsToSort = [...leads];

      switch (sortOption) {
        case "date-newest":
          return leadsToSort.sort((a, b) =>
            new Date(b.createdAt) - new Date(a.createdAt)
          );
        case "date-oldest":
          return leadsToSort.sort((a, b) =>
            new Date(a.createdAt) - new Date(b.createdAt)
          );
        case "confidence-high":
          return leadsToSort.sort((a, b) => {
            const aConfidence = a.confidence_level || 0;
            const bConfidence = b.confidence_level || 0;
            return bConfidence - aConfidence;
          });
        case "confidence-low":
          return leadsToSort.sort((a, b) => {
            const aConfidence = a.confidence_level || 0;
            const bConfidence = b.confidence_level || 0;
            return aConfidence - bConfidence;
          });
        default:
          return leadsToSort;
      }
    }, [leads, sortOption]);

    const count = sortedLeads.length;
    const { setNodeRef, isOver } = useDroppable({
      id: columnId,
    });

    const leadIds = sortedLeads.map((lead) => lead._id);
    const virtuosoRef = useRef(null);

    // Check if insert indicator should show in this column
    const showInsertIndicator =
      insertPosition && insertPosition.columnId === columnId;
    const insertIndex = showInsertIndicator ? insertPosition.index : -1;

    // Scroll to make insert position visible
    useEffect(() => {
      if (showInsertIndicator && virtuosoRef.current) {
        if (insertIndex > 0 && insertIndex < sortedLeads.length) {
          virtuosoRef.current.scrollToIndex({
            index: insertIndex,
            align: "center",
            behavior: "smooth",
          });
        } else if (insertIndex === 0) {
          virtuosoRef.current.scrollToIndex({
            index: 0,
            align: "start",
            behavior: "smooth",
          });
        }
      }
    }, [showInsertIndicator, insertIndex, sortedLeads.length]);

    // Build data array with ghost card placeholder
    const dataWithPlaceholder = useMemo(() => {
      if (!showInsertIndicator) {
        return sortedLeads;
      }

      // Create a placeholder lead object
      const placeholder = {
        _id: "__placeholder__",
        client_name: "",
        isPlaceholder: true,
      };

      // Insert placeholder at the correct position
      const newLeads = [...sortedLeads];
      newLeads.splice(insertIndex, 0, placeholder);
      return newLeads;
    }, [sortedLeads, showInsertIndicator, insertIndex]);

    // Stable itemContent function with ghost card
    const itemContent = useCallback(
      (index, lead) => {
        if (lead.isPlaceholder) {
          return (
            <PipelineCard key={lead._id} lead={lead} isPlaceholder={true} />
          );
        }

        return (
          <SortablePipelineCard
            key={lead._id}
            lead={lead}
            index={index}
            columnSettings={columnSettings}
            onViewDetails={onViewDetails}
            onContactClick={onContactClick}
            onMenuAction={onMenuAction}
            bulkSelectMode={bulkSelectMode}
            isSelected={selectedLeadIds.includes(lead._id)}
            onSelectToggle={onSelectToggle}
            isFirstColumn={isFirstColumn}
            isLastColumn={isLastColumn}
          />
        );
      },
      [onViewDetails, onContactClick, onMenuAction, columnSettings, bulkSelectMode, selectedLeadIds, onSelectToggle, isFirstColumn, isLastColumn],
    );

    return (
      <Box
        sx={{
          width: 340,
          minWidth: 340,
          bgcolor: colors.bgColumnHeader,
          borderRadius: "16px",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          border: `1px solid ${colors.borderLight}`,
          boxShadow: shadows.column,
          flexShrink: 0,
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            bgcolor: "#FFFFFF !important" ,
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: colors.primary,
            borderRadius: "4px",
            "&:hover": {
              bgcolor: colors.primaryDark,
            },
          },
        }}
      >
        {/* Column Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            p: 2,
            pb: 1.5,
            borderBottom: `1px solid ${colors.borderLight}`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 700,
                color: colors.textPrimary,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {title}
            </Typography>
            {bulkSelectMode && selectedLeadIds.length > 0 && (
              <Chip
                icon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
                label={`${selectedLeadIds.length} selected`}
                size="small"
                sx={{
                  height: 22,
                  bgcolor: theme.palette.primary.main,
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 600,
                  "& .MuiChip-icon": {
                    color: "#fff",
                  },
                }}
              />
            )}
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <PipelineColumnMenu
              columnId={columnId}
              sortOption={sortOption}
              onAction={onColumnMenuAction}
            />
            <Box
              sx={{
                minWidth: 24,
                height: 24,
                borderRadius: "6px",
                bgcolor: theme.palette.primary.main,
                color: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {count}
            </Box>
          </Stack>
        </Stack>

        {/* Cards Container with Virtualization */}
        <SortableContext items={leadIds} strategy={verticalListSortingStrategy}>
          <Box
            ref={setNodeRef}
            sx={{
              mt: 1,
              flex: 1,
              overflow: "hidden",
              bgcolor: isOver ? alpha(theme.palette.primary.main, 0.05) : "transparent",
              transition: "background-color 0.2s ease",
              minHeight: 0,
              position: "relative",
              "& ::-webkit-scrollbar": {
                width: "8px",
              },
              "& ::-webkit-scrollbar-track": {
                bgcolor: "#FFFFFF",
              },
              "& ::-webkit-scrollbar-thumb": {
                bgcolor: colors.primary,
                borderRadius: "4px",
                "&:hover": {
                  bgcolor: colors.primaryDark,
                },
              },
            }}
          >
            {/* Drop Zone Indicator - shows at top when dragging bulk cards */}
            {showDropZone && (
              <Box
                sx={{
                  mx: 1.25,
                  mt: 1.5,
                  mb: 0.75,
                  p: 2,
                  border: `2px dashed ${theme.palette.primary.main}`,
                  borderRadius: "10px",
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  animation: "pulse 1.5s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%, 100%": { opacity: 0.7 },
                    "50%": { opacity: 1 },
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                  }}
                >
                  Drop {dropZoneCount} card{dropZoneCount > 1 ? "s" : ""} here (first)
                </Typography>
              </Box>
            )}

            {sortedLeads.length === 0 && !showDropZone ? (
              <Box
                sx={{
                  border: `2px dashed ${theme.palette.primary.main}`,
                  borderRadius: "12px",
                  height: 120,
                  opacity: 0.3,
                  m: 1.5,
                }}
              />
            ) : sortedLeads.length > 0 ? (
              <Box
                sx={{
                  height: "100%",
                  "& > div": {
                    "&::-webkit-scrollbar": {
                      width: 6,
                    },
                    "&::-webkit-scrollbar-track": {
                      bgcolor: "#fff",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      bgcolor: theme.palette.primary.main,
                      borderRadius: 3,
                    },
                    scrollbarWidth: "thin",
                    scrollbarColor: `${theme.palette.primary.main} #fff`,
                  },
                }}
              >
                <Virtuoso
                  ref={virtuosoRef}
                  style={{ height: "100%", paddingTop: 8 }}
                  data={dataWithPlaceholder}
                  itemContent={itemContent}
                  defaultItemHeight={ITEM_HEIGHT}
                  components={{
                    Scroller,
                    Footer,
                  }}
                />
              </Box>
            ) : null}
          </Box>
        </SortableContext>
      </Box>
    );
  },
);

PipelineColumn.displayName = "PipelineColumn";
export default PipelineColumn;
